import request from '@wearetla/tla-essentials-tools/utilities/request';
import CreditCards from 'creditcards';
import { createI18nInstance } from '/utilities/i18n';

// Functions
import formatDate from '/functions/format-date';
import getCardTypeImage from '/functions/get-card-type-image';

const getT = () => createI18nInstance().t;

const userServices = {
	getUserData: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`user/me`, {}, (payload, status, request) => {
				if (status === 200 && payload?.user) {
					resolve({
						...payload.user,
						birthdate: payload.user.birthdate
							? payload.user.birthdate.split('-').reverse().join('/')
							: '',
						required_agreements: payload.required_agreements ?? [],
					});
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Kullanıcı bilgileri getirilirken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	getGuestToken: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`users/init`, {}, (payload, status, request) => {
				if (payload?.guest_token) {
					resolve(payload.guest_token);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Kullanıcı bilgisi getirilirken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	logout: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.delete(`users/logout`, {}, (payload, status, request) => {
				if (status === 200) {
					resolve();
				} else {
					reject(
						request?.data?.meta?.messages || t('Çıkış yapılırken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	// User Info
	updateInfo: (userData) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(`user/update`, { ...userData }, (payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages || {
							success: t('Üyelik bilgileriniz başarıyla güncellendi.'),
						}
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Üyelik bilgileriniz güncellenirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	deleteUser: (token) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(`user/delete`, { otp_token: token }, (payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages || {
							success: t('Silme talebinizi aldık. 15 gün içinde hesabınız silinecek.'),
						}
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Silme talebiniz alınırken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	deletePersonalData: (formFields) => {
		const t = getT();
		return new Promise((resolve, reject) => {
			request.post(
				`privacy/data-delete-apply`,
				{
					...formFields,
				},
				(payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages || {
							success: t('Silme talebinizi aldık. 15 gün içinde talebinizi işleme alacağız.'),
						}
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Silme talebiniz alınır bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	// Tickets
	getUserBookings: (mode = 'active', filters) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`user/orders/ticket/${mode}`, filters, (payload, status, request) => {
				if (status === 200 && payload.orders) {
					resolve({
						bookings: payload.orders,
						pagination: request.data.pagination,
					});
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Sözleşme getirilirken bir hata ile karşılaşıldı, lütfen daha sonra tekrar deneyin.')
					);
				}
			});
		});
	},
	getTicketPDF: (booking, ticket) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`order/detail/${booking.id}/ticket-qr/${ticket.qr_code}`,
				false,
				(payload, status, request) => {
					if (status === 200 && payload?.file_base64) {
						resolve(payload.file_base64);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('PDF indirilirken bir hata ile karşılaşıldı, lütfen daha sonra tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	refundTicket: () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve();
			}, 1000);
		});
	},
	// Agreements
	getAgreement: (key, parameter) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`agreement/${key}${parameter ? `/${parameter}` : ''}`,
				false,
				function (payload, status, request) {
					if (status === 200 && payload?.agreement) {
						resolve(payload.agreement);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Sözleşme getirilirken bir hata ile karşılaşıldı, lütfen daha sonra tekrar deneyin.')
						);
					}
				},
				{}
			);
		});
	},
	getAllAgreements: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`agreement/all`,
				false,
				function (payload, status, request) {
					if (status === 200 && payload?.agreements) {
						resolve(payload.agreements);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Sözleşmeler getirilirken bir hata ile karşılaşıldı, lütfen daha sonra tekrar deneyin.')
						);
					}
				},
				{}
			);
		});
	},
	updateAgreements: (agreements) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				`user/agreement/update`,
				{ agreements },
				function (payload, status, request) {
					if (status === 200) {
						resolve(payload);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Sözleşmeler onaylanırken bir hata ile karşılaşıldı, lütfen daha sonra tekrar deneyin.')
						);
					}
				},
				{}
			);
		});
	},
	// Notifications
	getNotificationCount: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`user/notifications/count`, {}, (payload, status, request) => {
				if (status === 200 && payload?.notification_count) {
					resolve(payload.notification_count);
				} else {
					reject(
						request?.data?.meta?.messages || t('Güncelleme yapılırken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	readAllNotifications: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`user/notifications/read-all`, {}, (payload, status, request) => {
				if (status === 200) {
					resolve(true);
				} else {
					reject(
						request?.data?.meta?.messages || t('Güncelleme yapılırken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	getNotifications: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`user/notifications`, {}, (payload, status, request) => {
				if (status === 200 && payload?.notifications) {
					resolve(
						payload.notifications.map((notification) => ({
							...notification,
							formatted_created_at: formatDate(notification.created_at, 'd MMMM yyyy, HH:mm'),
						}))
					);
				} else {
					reject(
						request?.data?.meta?.messages || t('Bildirimler getirilirken bir hata ile karşılaşıldı.')
					);
				}
			});
		});
	},
	// Payment
	getSavedCards: () => {
		return new Promise((resolve, reject) => {
			request.get(`user/card`, {}, (payload, status, request) => {
				if (status === 200 && payload?.credit_cards) {
					resolve(
						payload.credit_cards.map((card) => {
							const bin = card.card_masked_number.split('*')[0];
							const cardType = CreditCards.card.type(bin.replace(/}D/g, ''), true);
							return {
								...card,
								value: card.uuid,
								label: card.card_masked_number,
								bin,
								type: cardType,
								typeImage: getCardTypeImage(cardType),
							};
						})
					);
				} else {
					reject(request);
				}
			});
		});
	},
	removeSavedCard: (cardId) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(`user/card/delete/${cardId}`, {}, (payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages || { success: t('Kartınız başarıyla silindi.') }
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Kartınız silinirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	// OTP
	sendOTP: (phoneNumber, action = 'login', additionalFields = {}) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				`otp/send`,
				{
					phone: phoneNumber,
					token_type: `${action}_token`,
					...additionalFields,
				},
				(payload, status, request) => {
					if (status === 200) {
						resolve(payload);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('SMS gönderilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	checkOTP: (phoneNumber, code) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				`otp/verify`,
				{
					phone: phoneNumber,
					otp_code: code,
				},
				(payload, status, request) => {
					if (status === 200 && payload?.otp_token) {
						resolve(payload.otp_token);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('SMS onaylanırken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	login: (phoneNumber, otpToken) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				'auth/login',
				{
					phone: phoneNumber,
					otp_token: otpToken,
				},
				(payload, status, request) => {
					if (status === 200 && payload?.access_token) {
						resolve(payload);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Giriş işlemi gerçekleştirilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	register: (registerData) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				'auth/register',
				{
					...registerData,
				},
				(payload, status, request) => {
					if (status === 200 && payload?.access_token) {
						resolve(payload);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Üye kayıt işlemi gerçekleştirilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
};

export default userServices;
